import { ProFormaModelIds } from 'constants/proForma';
import { IModelExportConfig } from './types';

// ============================================
// Base Configuration for pro forma models
// ============================================

const BASE_CONFIG: IModelExportConfig = {
    buildingInfoTab: {
        propertyInfo: {
            dealName: 'Deal name',
            address: 'Address',
            city: 'City',
            state: 'State',
            zip: 'Zip',
            county: 'County',
        },
        buildingSpecs: {
            numberOfBuildings: 'Number of buildings',
            averageStories: 'Average stories',
            grossSF: 'Gross SF',
            rentableSF: 'Rentable SF',
            efficiencyRatio: 'Efficiency ratio',
            totalResidentialUnits: 'Total residential units',
        },
        parking: {
            surfaceSpaces: 'Surface spaces',
            structuredSpaces: 'Structured spaces',
            undergroundSpaces: 'Underground spaces',
            parkingRatio: 'Parking ratio',
            ratioLabel: 'per unit',
        },
        keyDates: {
            analysisStart: 'Analysis start',
            constructionStart: 'Construction start',
            constructionDuration: 'Construction duration',
            operationsStart: 'Operations start',
            holdPeriod: 'Hold period',
            projectedSaleDate: 'Projected sale date',
        },
        operatingAssumptions: {
            operationStartDate: 'Operation start date',
            occupancyUponStabilization: 'Occupancy upon stabilization',
            rentGrowthAnnual: 'Rent growth (annual)',
            percentPreLeased: '% pre-leased',
            leaseUpPaceUnitsPerMonth: 'Lease-up pace (units/month)',
            lengthOfLease: 'Length of lease',
            initialFreeRent: 'Initial free rent',
            stabilizedFreeRent: 'Stabilized free rent',
        },
    },
    sourcesUsesTab: {
        uses: {
            sectionHeader: 'DEVELOPMENT BUDGET (USES)',
            amountColumn: 'Amount',
            costPerGsfColumn: 'Cost / GSF',
            costPerUnitColumn: 'Cost / Unit',
            totalLabel: 'TOTAL USES',
        },
        sources: {
            sectionHeader: 'SOURCES OF CAPITAL',
            amountColumn: 'Amount',
            percentOfTotalColumn: '% of Total',
            rateColumn: 'Rate',
            totalLabel: 'TOTAL SOURCES',
        },
    },
    cashFlowTab: {
        income: {
            sectionHeader: 'INCOME',
            grossRentLabel: 'Gross Rent - Apartment Units',
            concessions: 'Concessions',
            gainLossToLease: 'Gain/Loss to Lease',
            nonRevenueUnits: 'Non-Revenue Units',
            totalRentalIncome: 'Total Rental Income',
            totalOtherIncome: 'Total Other Income',
            totalPotentialIncome: 'Total Potential Income',
        },
        adjustments: {
            sectionHeader: 'INCOME ADJUSTMENTS',
            totalIncomeAdjustments: 'Total Income Adjustments',
        },
        effectiveGrossRevenue: {
            sectionHeader: 'EFFECTIVE GROSS REVENUE',
        },
        expenses: {
            sectionHeader: 'OPERATING EXPENSES',
            totalOperatingExpenses: 'Total Operating Expenses',
        },
        netOperatingIncome: {
            sectionHeader: 'NET OPERATING INCOME',
        },
        capex: {
            sectionHeader: 'CAPITAL EXPENDITURES',
            totalCapitalExpenditures: 'Total Capital Expenditures',
        },
        cashFlowFromOperations: {
            sectionHeader: 'CASH FLOW FROM OPERATIONS',
        },
        occupancyMetrics: {
            sectionHeader: 'OCCUPANCY METRICS',
            leasedPercent: 'Leased %',
            economicOccupancy: 'Economic Occupancy %',
        },
    },
    dispositionTab: {
        assumptions: {
            saleDate: 'Sale Date:',
            sellingCosts: 'Selling Costs:',
            saleMonth: 'Sale Month:',
            marketCapRate: 'Market Cap Rate:',
        },
        stabilizedValuation: {
            sectionHeader: 'STABILIZED VALUATION',
            stabilizedValuation: 'Stabilized valuation',
            perRentableSf: 'Per rentable SF',
            perUnit: 'Per unit',
        },
        saleProceeds: {
            sectionHeader: 'SALE PROCEEDS',
            grossSaleProceeds: 'Gross Sale Proceeds',
            sellingCosts: 'Selling Costs',
            netSaleProceeds: 'Net Sale Proceeds',
            loanPayoff: 'Loan Payoff',
            equityProceedsFromSale: 'Equity Proceeds from Sale',
        },
    },
    returnsTab: {
        propertyReturns: {
            sectionHeader: 'PROPERTY-LEVEL RETURNS',
            irr: 'Internal Rate of Return (IRR)',
            equityMultiple: 'Equity Multiple (EMX)',
            roi: 'Return on Investment (ROI)',
        },
        developmentReturns: {
            sectionHeader: 'DEVELOPMENT RETURNS',
            yieldOnCost: 'Yield on cost',
            marketCapRate: 'Market cap rate',
            developmentSpread: 'Development spread',
        },
        keyStatistics: {
            sectionHeader: 'KEY STATISTICS',
            operatingExpenseRatio: 'Operating expense ratio',
            cashOnCashReturn: 'Cash-on-cash return',
            stabilizedNoi: 'Stabilized NOI',
        },
    },
};

// ============================================
// Model Config Registry
// ============================================

export const MODEL_EXPORT_CONFIGS: Record<number, IModelExportConfig> = {
    [ProFormaModelIds.APARTMENT]: BASE_CONFIG,
    // TODO: Add configs for APARTMENT_WITH_RETAIL, INDUSTRIAL, RETAIL, OFFICE, DATA_CENTER (extend BASE_CONFIG as needed)
};

export const getModelExportConfig = (modelId: number): IModelExportConfig => (
    MODEL_EXPORT_CONFIGS[modelId] ?? BASE_CONFIG
);
